import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { postChat } from '@/lib/api';
import { LOGOS } from '@/i18n';

function genSessionId() {
  try {
    const existing = localStorage.getItem('cauramigo_session');
    if (existing) return existing;
    const id = 'c-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem('cauramigo_session', id);
    return id;
  } catch {
    return 'c-' + Math.random().toString(36).slice(2, 10);
  }
}

const Chatbot = ({ lang, t }) => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  // Seed greeting whenever language changes and the welcome hasn't been shown yet in this tab
  useEffect(() => {
    setMessages([{ role: 'assistant', content: t.chatbot.greeting }]);
  }, [lang, t.chatbot.greeting]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await postChat({ session_id: sessionId, message: text, language: lang });
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: t.chatbot.error }]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <>
      {/* Launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-5 right-5 z-50 pulse-glow flex items-center gap-2 rounded-full bg-[rgb(var(--brand-electric))] text-white px-4 py-3 text-sm font-semibold shadow-lg hover:brightness-110"
            data-testid="cauramigo-launcher"
            aria-label={t.chatbot.launcher}
          >
            <MessageCircle size={18} />
            <span className="hidden sm:inline">{t.chatbot.launcher}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-5 right-5 z-50 w-[min(92vw,400px)] h-[min(75vh,620px)] flex flex-col rounded-2xl border border-white/10 bg-[rgb(var(--bg-1))] shadow-2xl overflow-hidden"
            data-testid="cauramigo-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[rgb(var(--bg-0))]">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img src={LOGOS.isologo_dark_bg} alt="Cauramigo" className="h-9 w-9 rounded-lg" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-[rgb(var(--bg-0))]" />
                </div>
                <div>
                  <div className="font-display text-sm font-semibold text-white flex items-center gap-1.5">
                    {t.chatbot.title} <Sparkles size={12} className="text-[rgb(var(--brand-electric))]" />
                  </div>
                  <div className="kicker-mono text-[10px] text-white/50">{t.chatbot.subtitle}</div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full hover:bg-white/5 text-white/70 hover:text-white grid place-items-center"
                aria-label="close"
                data-testid="cauramigo-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="cauramigo-messages">
              {messages.map((m, i) => (
                <div key={m.id || `${m.role}-${m.ts || i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-[rgb(var(--brand-electric))] text-white rounded-br-sm'
                        : 'bg-[rgb(var(--bg-2))] text-white/90 border border-white/10 rounded-bl-sm'
                    }`}
                    data-testid={`cauramigo-msg-${m.role}-${i}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[rgb(var(--bg-2))] border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-1.5" data-testid="cauramigo-typing">
                    <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--brand-electric))] animate-pulse" />
                    <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--brand-electric))] animate-pulse" style={{ animationDelay: '.15s' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--brand-electric))] animate-pulse" style={{ animationDelay: '.3s' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Quick prompts (only when 1 message) */}
            {messages.length <= 1 && !loading && (
              <div className="px-3 pb-2 flex flex-wrap gap-2" data-testid="cauramigo-quick">
                {t.chatbot.quick.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-white/80 hover:border-[rgba(5,126,243,.5)] hover:bg-[rgba(5,126,243,.08)] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={onSubmit} className="p-3 border-t border-white/10 bg-[rgb(var(--bg-0))] flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.chatbot.placeholder}
                className="field flex-1"
                data-testid="cauramigo-input"
                disabled={loading}
              />
              <button
                type="submit"
                className="h-11 w-11 grid place-items-center rounded-xl bg-[rgb(var(--brand-electric))] text-white shadow-[var(--glow-electric)] hover:brightness-110 disabled:opacity-50"
                disabled={loading || !input.trim()}
                data-testid="cauramigo-send-button"
                aria-label={t.chatbot.send}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
